import { JSONContent } from "@tiptap/react";

/**
 * Templates for "List As" assembly references. Both the reference's own
 * `template` and each table column's `template` are stored as rich-text JSON
 * (TipTap document JSON) and edited with the mention editor.
 *
 * `JSONContent` is the on-the-wire shape of the backend `RichTextJson` type.
 */
export type RichTextTemplate = JSONContent;

/**
 * True when a TipTap template document carries no meaningful content — i.e. it
 * is the empty base editor (`{ doc: [] }`, `{ doc: [paragraph] }`) or holds only
 * whitespace. The mention editor's `onUpdate` can replace a `null` template with
 * such an empty document (on focus/normalisation, or after the user types then
 * deletes everything), so we use this to avoid persisting an "empty" template
 * the user never actually authored.
 */
export const isTemplateEmpty = (template: RichTextTemplate | null | undefined): boolean => {
  if (!template) return true;
  const hasMeaningfulContent = (node: JSONContent): boolean => {
    if (node.type === "text") return Boolean(node.text?.trim());
    // Any node other than the structural doc/paragraph wrappers (e.g. a
    // mention, image or hard break) counts as real content.
    if (node.type && node.type !== "doc" && node.type !== "paragraph") return true;
    return (node.content ?? []).some(hasMeaningfulContent);
  };
  return !hasMeaningfulContent(template);
};

export interface AssemblyReferenceTemplateColumnWrite {
  name: string;
  template: RichTextTemplate;
}

export interface AssemblyReferenceTemplateColumn extends AssemblyReferenceTemplateColumnWrite {
  objectIdentifier: string;
  assemblyReferenceObjectIdentifier: string;
}
